<template>
  <QaPageShell :title="$t('_QACases')" :error="error" @retry="load">
    <template #filters>
      <QaFilterBar @change="reload" />
    </template>

    <v-card class="qa-card mt-3" variant="flat">
      <div class="d-flex align-center flex-wrap ga-3 pa-3">
        <!--
          Q9 — ตัวกรองนี้คือ "ผลตัดสิน QA" ไม่ใช่วงจรชีวิตของเคส (open/closed)
          ⚠️ เคยส่งค่า pass/fail ลงช่อง status แล้วได้ 0 แถวเงียบ ๆ — server เอาไปเทียบกับ
             open/closed · ตอนนี้ส่งลงช่อง qaStatus ที่ server รองรับแล้ว
          ปุ่ม "ยังไม่ประเมิน/รอประเมิน" มีไว้ให้หัวหน้างานไล่ดูว่าเคสไหนตกหล่นจาก pipeline
        -->
        <v-btn-toggle v-model="qaStatus" density="comfortable" variant="outlined" divided mandatory rounded="lg" color="primary" @update:model-value="reload">
          <v-btn value="all" size="small">{{ $t('qaAllChannels') }}</v-btn>
          <v-btn value="pass" size="small">{{ $t('qaPass') }}</v-btn>
          <v-btn value="borderline" size="small">{{ $t('qaBorderline') }}</v-btn>
          <v-btn value="fail" size="small">{{ $t('qaFail') }}</v-btn>
          <v-btn value="not-evaluated" size="small">{{ $t('qaNotEvaluatedFilter') }}</v-btn>
          <v-btn value="pending" size="small">{{ $t('qaPendingFilter') }}</v-btn>
        </v-btn-toggle>

        <v-text-field
          v-model="search"
          :placeholder="$t('qaSearchCasePlaceholder')"
          density="compact"
          variant="outlined"
          hide-details
          clearable
          prepend-inner-icon="mdi-magnify"
          style="max-width: 340px"
          @update:model-value="onSearch"
        />

        <v-spacer />
        <span class="text-caption text-medium-emphasis">{{ total }} เคส</span>
      </div>
      <v-divider />

      <div class="qa-table-wrap pt-3">
        <v-data-table-server
          :headers="headers"
          :items="items"
          :items-length="total"
          :loading="loading"
          :page="page"
          :items-per-page="pageSize"
          :items-per-page-options="[15, 25, 50, 100]"
          class="elevation-0 my-data-table qa-table qa-table-wide"
          density="comfortable"
          fixed-header
          hover
          :row-props="{ class: 'qa-row-click' }"
          @update:options="onOptions"
          @click:row="openCase"
        >
          <template #[`item.evaluatedAt`]="{ item }">
            <span class="qa-num text-body-2">{{ formatDateTime(item.evaluatedAt) }}</span>
          </template>
          <template #[`item.channel`]="{ item }"><QaChannelChip :channel="item.channel" /></template>
          <template #[`item.channelId`]="{ item }"><QaSocialChip :channel-id="item.channelId" /></template>
          <template #[`item.customerName`]="{ item }">
            <span class="text-body-2">{{ item.customerName || '—' }}</span>
          </template>
          <!-- ⚠️ 2 chip คนละความหมาย · ServiceType = สายงานที่ใช้จัดคิวฝั่ง Salesforce
               (Banking / CreditCard) มาจากบอทคัดแยก · หัวข้อ = สิ่งที่ AI อ่านจากบทสนทนา
               ห้ามยุบเป็นอันเดียว และห้ามให้ ServiceType แทนหัวข้อเมื่อหัวข้อยังว่าง -->
          <template #[`item.topicLabel`]="{ item }">
            <div class="d-flex align-center ga-1 flex-nowrap">
              <v-chip
                v-if="item.serviceType"
                size="small"
                label
                variant="outlined"
                class="text-medium-emphasis"
                :title="$t('qaServiceTypeHint')"
              >{{ item.serviceType }}</v-chip>
              <v-chip size="small" label variant="flat" :style="{ background: item.topicBg, color: item.topicColor }">{{ item.topicLabel }}</v-chip>
            </div>
          </template>
          <template #[`item.agentName`]="{ item }">
            <QaAgentCell :name="item.agentName" :initials="item.agentInitials" :color="item.agentColor" :size="30" />
          </template>
          <template #[`item.reason`]="{ item }">
            <span>{{ item.reason }}</span>
            <v-chip v-if="item.isGateFail" color="error" size="x-small" variant="tonal" label class="ml-2">{{ $t('qaGateFail') }}</v-chip>
          </template>
          <template #[`item.durationSec`]="{ item }"><span class="qa-num">{{ formatDuration(item.durationSec) }}</span></template>
          <!-- totalScore = null ได้ (ยังไม่ประเมิน/ข้าม) — QaScoreCell แสดง "—" เอง ห้ามแปลงเป็น 0 -->
          <template #[`item.totalScore`]="{ item }"><QaScoreCell :score="item.totalScore" :status="item.qaStatus" /></template>
          <template #[`item.status`]="{ item }">
            <div class="d-flex align-center ga-1 flex-nowrap">
              <!-- qaStatus จากของจริง (รวมสถานะใหม่ pending/not-evaluated/skipped/failed) ·
                   status เป็นชื่อเก่าที่ฝั่ง mock ยังใช้ -->
              <!-- failed/skipped ต้องมองเห็นจากหน้ารายการ — ถ้าโชว์แต่ผลตัดสิน (not-evaluated)
                   งานที่ล้มจะจมหายไม่มีใครกดประเมินใหม่ -->
              <QaStatusChip :status="item.evalStatus === 'failed' || item.evalStatus === 'skipped' ? item.evalStatus : (item.qaStatus || item.status)" />
              <!-- ธง "ต้องให้คนดู" — ไอคอนเล็กพอให้กวาดตาเจอ ไม่กินที่คอลัมน์ -->
              <v-tooltip v-if="item.needsHumanReview" location="bottom" :text="$t('qaNeedsHumanReview')">
                <template #activator="{ props }">
                  <v-icon v-bind="props" size="16" color="warning">mdi-account-alert-outline</v-icon>
                </template>
              </v-tooltip>
            </div>
          </template>
          <template #no-data>
            <div class="text-center py-8 text-medium-emphasis">
              <v-icon size="34" color="grey-lighten-1">mdi-tray-remove</v-icon>
              <div class="text-body-2 mt-2">{{ $t('qaNoData') }}</div>
            </div>
          </template>
        </v-data-table-server>
      </div>
    </v-card>
  </QaPageShell>
</template>

<script>
/**
 * Case Explorer
 *
 * ใช้ v-data-table-server ตั้งแต่ต้น — กรอง/เรียง/แบ่งหน้าทำที่ server ทั้งหมด
 * เพราะ paging/filter/search ทำที่ชั้น service (กฎข้อ 6 ของแผน)
 * วันที่ต่อ API จริงจึงไม่ต้องเขียนหน้านี้ใหม่
 */
import qaConsole from '@/services/qaConsole';
import { formatDateTime, formatDuration } from '@/utils/qaFormat';
import QaPageShell from '@/components/QAConsole/QaPageShell.vue';
import QaFilterBar from '@/components/QAConsole/QaFilterBar.vue';
import QaAgentCell from '@/components/QAConsole/QaAgentCell.vue';
import QaScoreCell from '@/components/QAConsole/QaScoreCell.vue';
import QaStatusChip from '@/components/QAConsole/QaStatusChip.vue';
import QaChannelChip from '@/components/QAConsole/QaChannelChip.vue';
import QaSocialChip from '@/components/QAConsole/QaSocialChip.vue';

export default {
  name: 'QACases',
  components: { QaPageShell, QaFilterBar, QaAgentCell, QaScoreCell, QaStatusChip, QaChannelChip, QaSocialChip },
  data() {
    return {
      loading: true,
      error: null,
      items: [],
      total: 0,
      page: 1,
      pageSize: 25,
      sortBy: 'evaluatedAt',
      sortDir: 'desc',
      /** ผลตัดสิน QA (all|pass|borderline|fail|not-evaluated|pending) — คนละช่องกับ open/closed */
      qaStatus: 'all',
      search: '',
      searchTimer: null,
    };
  },
  computed: {
    headers() {
      return [
        { title: this.$t('qaDateTime'), key: 'evaluatedAt', width: '180px' },
        { title: this.$t('qaType'), key: 'channel', sortable: false, width: '110px' },
        // ช่องทางโซเชียลเป็นคนละเรื่องกับประเภทงาน — ดูคอมเมนต์ใน QaSocialChip.vue
        { title: this.$t('qaSocialChannel'), key: 'channelId', sortable: false, width: '130px' },
        // ชื่อลูกค้ามาจากโปรไฟล์ที่ดึงตอนรับ webhook (api/webhooks/line) ไม่ใช่จาก Salesforce
        { title: this.$t('qaCustomer'), key: 'customerName', sortable: false, minWidth: '150px' },
        // ⚠️ minWidth 250 ไม่ใช่ 190 — ช่องนี้ถือ 2 chip แล้ว (ServiceType + หัวข้อ)
        //    ถ้าคืนเป็น width คงที่ chip จะถูกบีบจนอ่านไม่ออกตอนสายงานมีค่า
        { title: this.$t('qaTopic'), key: 'topicLabel', sortable: false, minWidth: '250px' },
        { title: this.$t('qaAgent'), key: 'agentName', sortable: false, minWidth: '190px' },
        { title: this.$t('qaCaseReason'), key: 'reason', sortable: false, minWidth: '220px' },
        { title: this.$t('qaDuration'), key: 'durationSec', align: 'end', width: '110px' },
        { title: this.$t('qaQaScore'), key: 'totalScore', align: 'end', width: '120px' },
        { title: this.$t('qaResult'), key: 'status', sortable: false, width: '130px' },
      ];
    },
  },
  methods: {
    formatDateTime,
    formatDuration,
    onOptions(options) {
      this.page = options.page;
      this.pageSize = options.itemsPerPage;
      if (options.sortBy && options.sortBy.length) {
        this.sortBy = options.sortBy[0].key;
        this.sortDir = options.sortBy[0].order;
      }
      this.load();
    },
    onSearch() {
      // debounce กันยิง service ทุกตัวอักษร
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => this.reload(), 300);
    },
    reload() {
      this.page = 1;
      this.load();
    },
    async load() {
      this.loading = true;
      this.error = null;
      try {
        const res = await qaConsole.getCases({
          ...this.$store.getters['qaConsole/filter'],
          // ⚠️ ต้องเป็นช่อง qaStatus — ช่อง status ของ API คือ open/closed
          //    (บั๊กเดิม: ส่ง pass/fail ลง status แล้วได้ 0 แถวโดยไม่มี error ให้เห็น)
          qaStatus: this.qaStatus,
          q: this.search || undefined,
          page: this.page,
          pageSize: this.pageSize,
          sortBy: this.sortBy,
          sortDir: this.sortDir,
        });
        this.items = res.items;
        this.total = res.total;
      } catch (err) {
        this.error = err;
        this.items = [];
        this.total = 0;
      } finally {
        this.loading = false;
      }
    },
    openCase(_event, { item }) {
      this.$router.push({ name: 'QACaseDetail', query: { caseNo: item.caseNo, from: 'QACases' } });
    },
  },
};
</script>
